import { memo, useEffect, useRef, useState } from 'react'
import { CSSProperties } from 'react'

import Download from '@carbon/icons-react/lib/Download'
import {
  Partner,
  CustomerFindInvoiceFragment,
  useCustomerGetInvoicePdfLinkLazyQuery,
  useFindInvoiceLazyQuery,
  InvoicePdfStatus
} from 'graph/generated/payments/graphql-types'
import Text from 'lib/ui/Text'
import ToastMessage from 'lib/ui/Toast/v2'
import toast from 'react-hot-toast'
import { IReactToPrintProps, useReactToPrint } from 'react-to-print'
import { actionTracking, errorTracking } from 'utils/posthog'

import { useCompanyAndInvoiceContext } from '@/hooks/providers/CompanyAndInvoiceProvider'
import Button, { IButtonProps } from '@/ui/Button/v2'

import {
  PDF_UNAVAILABLE_TITLE,
  pdfUnavailableSubtitle
} from './pdfUnavailableMessage'
import { formatIcon } from './styles'
import InvoicePdfLayoutV2 from './v2/InvoicePdfLayout'
import * as css from './v2/styles'

const START_INTERVAL = 5_000 // 5 seconds
const EXPONENTIAL_RATE = 2
const MAX_ATTEMPTS = 5

interface IInvoiceDownload {
  autoDownload?: boolean
  autoDownloadWithButton?: boolean
  btnProps?: IButtonProps
  rootStyles?: CSSProperties
  btnText?: string
  invoice: CustomerFindInvoiceFragment
  onDownloaded?: () => void
  showLink?: boolean
  showIcon?: boolean
  buttonSize?: 'sm' | 'md' | 'lg'
  ignoreMargin?: boolean
  buttonIcon?: JSX.Element
}

const InvoiceDownload = memo(
  ({
    autoDownload = false,
    autoDownloadWithButton = false,
    btnText,
    invoice,
    btnProps,
    rootStyles,
    onDownloaded,
    showLink = false,
    showIcon = true,
    buttonSize = 'md',
    ignoreMargin = false,
    buttonIcon
  }: IInvoiceDownload) => {
    // print
    const componentRef = useRef(null)
    const handlePrintProps: IReactToPrintProps = {
      content: () => componentRef.current
    }
    handlePrintProps.pageStyle = css.reactToPrintPageStyle
    const handlePrint = useReactToPrint(handlePrintProps)

    // partner data
    const partner = invoice?.partner as Partner
    const { brandColor, company } = useCompanyAndInvoiceContext()
    const allowPDFFallback = company?.pdfFallbackEnabled ?? true

    // Shown instead of the generated PDF when the partner has the fallback off.
    // Copy and the missing-company degradation live in pdfUnavailableMessage.ts,
    // where they are unit tested without mocking this component's dependencies.
    const notifyPdfUnavailable = () =>
      toast.error(
        <ToastMessage
          type='error'
          title={PDF_UNAVAILABLE_TITLE}
          subtitle={pdfUnavailableSubtitle(company?.name)}
        />,
        { id: 'error' }
      )

    // List rows hand this component a slim invoice without the bill-to, so
    // fetch the full invoice by id, only when our own PDF is about to print
    // (PRD-1831). Best effort: on failure it prints what the row has.
    const [findInvoice] = useFindInvoiceLazyQuery()
    const [printInvoice, setPrintInvoice] =
      useState<CustomerFindInvoiceFragment>()
    const [printRequest, setPrintRequest] = useState(0)
    useEffect(() => {
      if (printRequest) handlePrint()
      // handlePrint changes identity every render; print once per request.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [printRequest])

    const printFallback = async () => {
      const result = await findInvoice({
        variables: { id: invoice?.id }
      }).catch(() => undefined)
      if (result?.data?.FindInvoice) setPrintInvoice(result.data.FindInvoice)
      setPrintRequest(n => n + 1)
    }

    // What to serve when there is no partner PDF: the generated one, or an
    // honest message when the partner has that turned off. One helper so the
    // policy lives in a single place rather than at each call site.
    const serveFallback = () =>
      allowPDFFallback ? printFallback() : notifyPdfUnavailable()

    // fetch
    const [isPolling, setIsPolling] = useState(false)
    const [attempts, setAttempts] = useState(0)
    const [fetchInvoiceDownload, { data, loading: loadingInvoice, error }] =
      useCustomerGetInvoicePdfLinkLazyQuery({
        variables: { invoiceID: invoice?.id },
        fetchPolicy: 'cache-and-network'
      })

    // handle fetch response
    useEffect(() => {
      if (loadingInvoice || (data === undefined && error === undefined)) return

      if (error) {
        if (error?.message === 'Internal Server Error') {
          toast.error(
            <ToastMessage
              type='error'
              title='Invoice PDF is not yet synchronized'
              subtitle='Please try again shortly'
            />,
            { id: 'error' }
          )
          return
        }

        toast.error(
          <ToastMessage
            type='error'
            title='Could not get invoice PDF'
            subtitle='Please try again shortly'
          />,
          { id: 'error' }
        )
        errorTracking({
          fingerprint: 'InvoiceDownload',
          error: error,
          properties: {
            invoiceID: invoice?.id,
            partnerID: partner?.id,
            allowPDFFallback
          }
        })
        return
      }

      const status = data?.CustomerGetInvoicePdfLink?.status
      const url = data?.CustomerGetInvoicePdfLink?.url

      if (
        [InvoicePdfStatus.Pending, InvoicePdfStatus.Expired].includes(status)
      ) {
        // retry backoff
        setTimeout(
          () => setAttempts(prev => prev + 1),
          START_INTERVAL * Math.pow(EXPONENTIAL_RATE, attempts)
        )
        actionTracking({
          action: 'InvoiceDownloadRetry',
          properties: {
            invoice,
            partnerID: partner?.id,
            data,
            allowPDFFallback
          }
        })

        return
      }

      actionTracking({
        action: 'InvoiceDownloaded',
        properties: {
          invoice,
          partnerID: partner?.id,
          data,
          allowPDFFallback
        }
      })

      if (status === InvoicePdfStatus.Failure) {
        setIsPolling(false)

        toast.error(
          <ToastMessage
            type='error'
            title='Could not get invoice PDF'
            subtitle='Please try again shortly'
          />,
          { id: 'error' }
        )
        return
      }

      if (status === InvoicePdfStatus.Success) {
        setIsPolling(false)

        if (url) {
          window.open(url, '_blank')
          if (onDownloaded) onDownloaded()
        } else {
          serveFallback()
        }
        return
      }

      serveFallback()
      // Deliberately keyed on the query result alone. The body opens a window,
      // fires a toast and emits tracking events, so re-running it because an
      // unrelated prop changed identity would duplicate all three.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, loadingInvoice, error])

    const handleDownload = async () => {
      try {
        setIsPolling(true)
        await fetchInvoiceDownload()
      } catch (error) {
        errorTracking({
          fingerprint: 'InvoiceDownload',
          error: error,
          properties: {
            invoiceID: invoice?.id,
            partnerID: partner?.id
          }
        })
      }
    }

    useEffect(() => {
      if (!attempts) {
        return
      }
      if (attempts >= MAX_ATTEMPTS) {
        setIsPolling(false)
        setAttempts(0)
        toast.error(
          <ToastMessage
            type='error'
            title='Could not get invoice PDF'
            subtitle='Please try again shortly'
          />,
          { id: 'error' }
        )
        return
      }

      handleDownload()
    }, [attempts])

    useEffect(() => {
      if (autoDownload || autoDownloadWithButton) {
        handleDownload()
      }
    }, [autoDownload, autoDownloadWithButton])

    return (
      <>
        <div css={{ ...rootStyles }}>
          {(!autoDownload || autoDownloadWithButton) && (
            <Button
              onClick={handleDownload}
              loading={loadingInvoice || isPolling}
              variant='outline'
              buttonSize={buttonSize}
              iconPlacement='left'
              display='flex'
              {...btnProps}
            >
              {showIcon && (
                <span
                  css={{
                    ...(ignoreMargin
                      ? { '& svg': { margin: 0 } }
                      : { formatIcon })
                  }}
                >
                  {buttonIcon || <Download size={16} />}
                </span>
              )}
              {btnText && <Text>{btnText}</Text>}
            </Button>
          )}
          {showLink && data?.CustomerGetInvoicePdfLink?.url && (
            <Text size='sm' margin={[0.5, 0, 0]}>
              If you have trouble downloading the invoice you can click {''}
              <a href={data?.CustomerGetInvoicePdfLink?.url}>this link</a>.
            </Text>
          )}
        </div>

        <div ref={componentRef} css={css.showOnlyOnPrint}>
          {invoice && partner && (
            <InvoicePdfLayoutV2
              partner={partner}
              invoice={printInvoice ?? invoice}
              brandColor={brandColor}
            />
          )}
        </div>
      </>
    )
  }
)

InvoiceDownload.displayName = 'InvoiceDownload'

export default InvoiceDownload
